import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { notification } from "antd";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";

import { cardExclusiveUrl, featureFlags, mainUrl } from "@settings";
import { useClientSettings } from "@hooks/useClientSettings";
import useAuth from "@hooks/useAuth";
import { CorbanOption, useCorbansOptions } from "@hooks/useCorbansOptions";
import { isCardProductType } from "@utils/card";
import Link from "@components/Link";
import { AuthLoading } from "@components/Auth/Loading";
import { useApiAuthSelectCorban } from "@hooks/api/auth";
import useEvent from "react-use-event-hook";
import { LOCAL_STORAGE } from "@constants";
import axios from "axios";
import { ApiInterceptorError } from "@classes/ApiInterceptorError";

type CorbanContext = {
  corbans: CorbanOption[];
  selectedCorban: CorbanOption | null;
  onSelectCorban: (corban: CorbanOption) => void;
  loading: boolean;
  isFetched: boolean;
  isReady: boolean;
  needCorbanSelection: boolean;
  corbanNeedsRedirection: (corban: CorbanOption) => boolean;
};

export const CorbanContext = createContext<CorbanContext>({
  corbans: [],
  selectedCorban: null,
  onSelectCorban: () => {},
  loading: false,
  isFetched: false,
  isReady: false,
  needCorbanSelection: false,
  corbanNeedsRedirection: () => false,
});

const hasNonCardProduct = (corban: CorbanOption) => {
  return corban.produtos?.some((product) => {
    return !isCardProductType(product.tipo_produto);
  });
};

const getCorbanUrl = (corban: CorbanOption) => {
  if (featureFlags.enableCorrespondenteBanqueiro && corban.url)
    return corban.url;
  if (hasNonCardProduct(corban)) {
    return mainUrl;
  } else {
    return cardExclusiveUrl;
  }
};

const buildCorbanUrl = (corban: CorbanOption) => {
  if (!corban) return "";
  const url = getCorbanUrl(corban);
  const corbanUrl = new URL(`/?cid=${corban.id}`, url);
  return corbanUrl.href;
};

export const getApiErrorStatus = (error: unknown) => {
  if (axios.isAxiosError(error)) return error.response?.status;

  if (
    error instanceof ApiInterceptorError &&
    axios.isAxiosError(error.sourceError)
  ) {
    return error.sourceError.response?.status;
  }
};

const useDefaultSelectedCorbanId = () => {
  const [search] = useSearchParams();
  const { onLogout } = useAuth();

  const [cid, setCID] = useState(() => search.get("cid"));
  const corbanId = !!cid && parseInt(cid);

  const reset = useCallback(() => setCID(null), []);

  useEffect(() => {
    if (corbanId) onLogout();
  }, [onLogout, corbanId]);

  return [corbanId || null, reset] as const;
};

export const CorbanContextProvider = ({ children }: PropsWithChildren) => {
  const client = useClientSettings();
  const [notificationApi, contextHolder] = notification.useNotification();

  const [redirectingCorban, setRedirectingCorban] = useState<CorbanOption>();
  const isRedirecting = !!redirectingCorban;
  const { user, onLogout, setCorbanProducts, updateUser } = useAuth();
  const isAuthenticated = !!user?.accessToken;

  const {
    options,
    loading: isLoadingOptions,
    isFetched,
    error: corbansError,
  } = useCorbansOptions({
    skipFetch: isRedirecting || !isAuthenticated,
  });

  const [defaultCorbanId, resetDefaultCorban] = useDefaultSelectedCorbanId();
  const defaultCorban = useMemo(
    () =>
      !isAuthenticated || !defaultCorbanId
        ? null
        : (options.find((o) => o.id === defaultCorbanId) ?? null),
    [defaultCorbanId, isAuthenticated, options],
  );

  const selectedCorbanId = user?.corbanId;
  const selectedCorban = useMemo(
    () => options.find((o) => o.id === selectedCorbanId) ?? null,
    [selectedCorbanId, options],
  );
  const [selectCorban, { loading: isLoadingCorbanSelection }] =
    useApiAuthSelectCorban();

  const onSelectCorban = useCallback(
    (corban: CorbanOption) => {
      updateUser({
        corbanId: corban.id,
        corbanName: corban.name,
      });
    },
    [updateUser],
  );

  const redirectTo = useCallback(
    (url?: string) => {
      if (!url) return;
      onLogout(url);
    },
    [onLogout],
  );

  const notify = useCallback(() => {
    if (!user?.name) return;
    notificationApi.info({
      icon: <InfoCircleTwoTone />,
      message: `Olá, ${user.name}!`,
      description: (
        <>
          Você está logado no ambiente: <strong>{user.corbanName}</strong>
        </>
      ),
    });
  }, [notificationApi, user?.corbanName, user?.name]);

  const updateTokenWithCorban = useEvent(async () => {
    if (!selectedCorban || !isAuthenticated || isLoadingCorbanSelection) return;
    if (user.tokenHasDataOfCorbanId === selectedCorban.id) return;

    const { data } = await selectCorban({
      data: { id: selectedCorban.id },
    });
    const isCognito = !!localStorage.getItem(
      LOCAL_STORAGE.COGNITO_REFRESH_TOKEN,
    );
    updateUser({
      accessToken: data.access,
      ...(!isCognito && { refreshToken: data.refresh }),
      tokenHasDataOfCorbanId: selectedCorban.id,
    });
  });

  const corbanNeedsRedirection = useCallback((corban: CorbanOption) => {
    const url = getCorbanUrl(corban);
    return !!(url && url !== location.origin);
  }, []);

  const isReady = !!(
    user?.tokenHasDataOfCorbanId &&
    user.tokenHasDataOfCorbanId === selectedCorban?.id
  );

  const shouldSkipCorbanNotification =
    (!!user?.enableFaceMatch && !user?.isChecked && !!user?.checkUrl) ||
    !!user?.validateRegister?.shouldValidateLink ||
    !!user?.validateRegister?.isAttention ||
    !!user?.validateRegister?.isRejected;

  const needCorbanSelection =
    !selectedCorban && !defaultCorban && options?.length > 1;

  const isLoadingCorbanToken = !needCorbanSelection && !isReady;

  const isLoadingDefaultCorban =
    defaultCorban && defaultCorban !== selectedCorban;

  const loading =
    !user ||
    !isFetched ||
    isLoadingOptions ||
    isLoadingDefaultCorban ||
    isLoadingCorbanToken;

  useEffect(() => {
    if (!isAuthenticated) return;
    if (getApiErrorStatus(corbansError) === 400) {
      onLogout();
    }
  }, [corbansError, isAuthenticated, onLogout]);

  const contextState = useMemo<CorbanContext>(
    () => ({
      loading,
      isFetched,
      isReady,
      selectedCorban,
      onSelectCorban,
      corbans: options,
      needCorbanSelection,
      corbanNeedsRedirection,
    }),
    [
      needCorbanSelection,
      isFetched,
      isReady,
      selectedCorban,
      onSelectCorban,
      loading,
      options,
      corbanNeedsRedirection,
    ],
  );

  useEffect(() => {
    if (selectedCorban || !isAuthenticated) return;
    if (options.length === 1) {
      onSelectCorban(options[0]);
    } else if (defaultCorban) {
      onSelectCorban(defaultCorban);
      resetDefaultCorban();
    }
  }, [
    options,
    selectedCorban,
    defaultCorban,
    isAuthenticated,
    onSelectCorban,
    resetDefaultCorban,
  ]);

  useEffect(() => {
    if (!user?.corbanId || !selectedCorban) return;

    if (corbanNeedsRedirection(selectedCorban)) {
      setRedirectingCorban(selectedCorban);
      redirectTo(buildCorbanUrl(selectedCorban));
    } else {
      void updateTokenWithCorban();
    }
  }, [
    selectedCorban,
    user?.corbanId,
    updateTokenWithCorban,
    redirectTo,
    corbanNeedsRedirection,
  ]);

  const isntReady =
    !isReady ||
    !selectedCorban ||
    isRedirecting ||
    shouldSkipCorbanNotification;

  useEffect(() => {
    if (isntReady) return;
    setCorbanProducts(selectedCorban?.produtos ?? []);
    notify();
  }, [selectedCorban, setCorbanProducts, notify, isntReady]);

  const apiUrlV2 = localStorage.getItem("apiUrlOriginacao") === "v2";

  return (
    <CorbanContext.Provider value={contextState}>
      <Helmet>
        <link rel="icon" type="image/svg+xml" href={client.assets.favicon} />
        <title>{apiUrlV2 ? `v2 - ${client.name}` : client.name}</title>
      </Helmet>
      {isRedirecting ? (
        <AuthLoading
          title={`Redirecionando para "${redirectingCorban.name}"`}
          subTitle={
            <>
              <span>Você está sendo redirecionado para </span>
              <Link to={buildCorbanUrl(redirectingCorban)}>
                {getCorbanUrl(redirectingCorban)}
              </Link>
            </>
          }
        />
      ) : (
        <>{children}</>
      )}
      {contextHolder}
    </CorbanContext.Provider>
  );
};
